/**
 * Op application (W1-03 core-model-ops; ARCHITECTURE.md §5.2): deliberately dumb and fast.
 *
 * No business rules: `ins` adds the record, `del` removes it, `set` / `single` apply the shallow patch (`undefined`
 * removes a field), `order` replaces the list, `idSeq` raises the counter (it never goes down, so its inverse is
 * itself). Every derived index is updated incrementally. Structural misuse (inserting an existing id, deleting or
 * patching a missing record, patching `id`) throws: it is a programming error, never a user-facing condition.
 *
 * Transport: a patch value `undefined` means "remove the field" (and a `prev` value `undefined` means "was absent"),
 * so ops travel by structured clone (postMessage, IndexedDB), which keeps `undefined` members and -0; a plain JSON
 * round trip drops them and would make the inverse of such a set unable to remove the field again. Ops that must
 * become JSON go through the lossless codec `opsToJson` / `opsFromJson` (core/ops/opsJson.ts, CR-W1-03-1).
 */

import { applyPatch, fieldOf } from '../ops/equal';
import type { Op } from '../ops/types';
import type { SingletonName, SingletonRecords } from '../model/types';
import type { DocIndexStore } from './indexes';
import { anyTable, setOrderList, setSingleton, type DocState } from './state';

export class ApplyError extends Error {
  override readonly name = 'ApplyError';
}

/** Applies one op to a state and its indexes. */
export function applyOp(s: DocState, idx: DocIndexStore, op: Op): void {
  switch (op.o) {
    case 'ins': {
      const t = anyTable(s, op.tbl);
      const id = op.rec.id;
      if (t.has(id)) throw new ApplyError(`apply ins: ${op.tbl} record ${String(id)} already exists`);
      t.set(id, op.rec);
      idx.added(op.tbl, op.rec);
      return;
    }
    case 'del': {
      const t = anyTable(s, op.tbl);
      const id = op.rec.id;
      const cur = t.get(id);
      if (cur === undefined) throw new ApplyError(`apply del: ${op.tbl} record ${String(id)} does not exist`);
      t.delete(id);
      idx.removed(op.tbl, cur);
      return;
    }
    case 'set': {
      const t = anyTable(s, op.tbl);
      const cur = t.get(op.id);
      if (cur === undefined) throw new ApplyError(`apply set: ${op.tbl} record ${String(op.id)} does not exist`);
      const nextId = fieldOf(op.next, 'id');
      if (nextId !== undefined && nextId !== op.id)
        throw new ApplyError(`apply set: cannot change the id of ${op.tbl} record ${String(op.id)}`);
      const next = applyPatch(cur, op.next);
      if (next === cur) return;
      t.set(op.id, next);
      idx.changed(op.tbl, cur, next);
      return;
    }
    case 'single': {
      applySingle(s, idx, op.key, op.next);
      return;
    }
    case 'order': {
      setOrderList(s, op.list, op.next);
      idx.orderChanged(op.list, op.next);
      return;
    }
    case 'idSeq': {
      if (op.next > s.idSeq) s.idSeq = op.next;
      return;
    }
  }
}

function applySingle(s: DocState, idx: DocIndexStore, key: SingletonName, patch: object): void {
  const cur: SingletonRecords[SingletonName] = s[key];
  const next = applyPatch(cur, patch);
  if (next === cur) return;
  setSingleton(s, key, next);
  idx.singletonChanged(key, cur, next);
}
